import React from "react";
import axios from '../axios';
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import ModalAlert from '../redux/components/modal/modal_alert';
import ModalLoader from '../redux/components/modal/modal_loader';
import "babel-polyfill";

export const ReviseOutlineModal = ({originalTitle, originalBody, eventId}) => {
  const modalRef = React.useRef();
  const errorModalRef = React.useRef();
  const rateLimitModalRef = React.useRef();
  const loaderRef = React.useRef();
  const titleRef = React.useRef(); 
  const bodyRef = React.useRef(); 

  const [hasRevisedOnce, setHasRevisedOnce] = React.useState(false);
  const [eventTitle, setEventTitle] = React.useState('');
  const [eventBody, setEventBody] = React.useState('');
  const [point, setPoint] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [overall, setOverall] = React.useState('');

  React.useEffect(() => {
    setEventTitle(originalTitle);
    setEventBody(originalBody);
  }, [originalTitle, originalBody]);

  const copyToClipboard = async (ref) => {
    try {
      if (ref?.current) {
        await navigator.clipboard.writeText(ref.current.value);
        alert('クリップボードにコピーしました');
      }
    } catch (err) {
      console.error('コピーに失敗しました', err);
      alert('コピーに失敗しました');
    }
  };
  
  const formatText = (text) => {
    return text
      .split('\n')
      .filter((line) => line.trim() !== '')
      .map((line, i) => {
        if (line.startsWith('【') && line.endsWith('】')) {
          return <p key={i} className="mt-2">{line}</p>;
        } else if (/^\d+\./.test(line)) {
          return <p key={i} className="mt-2"><strong>{line}</strong></p>;
        } else {
          return <p key={i}>{line}</p>;
        }
      });
  };

  const close = () => {
    modalRef.current?.closeModal();
  };

  const resultRef = React.useRef(null);

  const handleOpenModal = async () => {
    if (!eventId) {
      console.log('eventId:', eventId);
      modalRef.current?.openModal();
      return;
    }
  
    try {
      const res = await axios.get('/api/event/chatgpt_usage_log_latest', {
        params: { event_id: eventId },
      });
  
      if (res.data?.success) {
        const { point, comment, overall, title, body } = res.data.result;
        setPoint(point);
        setComment(comment);
        setOverall(overall);
        setEventTitle(title);
        setEventBody(body);
        setHasRevisedOnce(true); // 添削済み表示
      } else {
        setHasRevisedOnce(false); // 添削前表示
      }
  
      modalRef.current?.openModal();
    } catch (error) {
      if (error.response?.status === 404) {
        setHasRevisedOnce(false);
      } else {
        console.error('履歴取得に失敗しました:', error);
        setHasRevisedOnce(false);
      }
      modalRef.current?.openModal();
    } finally {
      loaderRef.current?.closeModal();
    }
  };

  const handleReviseClick = async () => {
    loaderRef.current?.openModal(); // ローディング表示
    try {
      const res = await axios.post('/api/event/revise_outline', {
        title: originalTitle,
        body: originalBody,
        event_id: eventId,
      });

      const { 
        point,
        comment,
        overall,
        title: revised_title,
        body: revised_body
      } = res.data?.result || {};

      setPoint(point);
      setComment(comment);
      setOverall(overall);
      setEventTitle(revised_title);
      setEventBody(revised_body);

      setHasRevisedOnce(true);

      setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);

    } catch (error) {
      console.error(error);
      if (error.response?.status === 429) {
        rateLimitModalRef.current?.openModal();
      } else {
        errorModalRef.current?.openModal();
      }
    } finally {
      loaderRef.current?.closeModal();
    }
  };

  const previewContent = (
    <button 
      type="button"
      className="a-button a-button--sm a-button--orange"
      style={{borderRadius: 4}}
      onClick={handleOpenModal}
      >添削する</button>);

  const modalContent = (
    <div>
        <div
            className="bg-white w-100 border-bottom"
            style={{
            zIndex: 9998,
            borderRadius: '16px 16px 0 0',
            position: 'fixed',
            }}
        >
            <button
            type="button"
            className="close close__modal position--modalclose"
            aria-label="Close"
            onClick={() => close()}
            >
                <span className="text-fz-24px float-right m-2" aria-hidden="true">
                ×
                </span>
            </button>
        </div>
        <div
            className="card bg-gray"
            style={{ paddingTop: '38px', paddingBottom: '144px' }}
        >
            <div className="shadow-sm mb-3 bg-white container col-md-8 col-lg-6">
            {!hasRevisedOnce ? (
            // 添削前の表示
            <div className="row justify-content-center p-3_5">
              <h6 className="mt-3">イベントのタイトルと説明文を添削します</h6>
              <p className="mt-3 text-fz-14px">
                ここでは、あなたが書いたイベントページの内容をAIが添削します。つなげーとで成功しているイベントの要素を取り入れ、成功率の高いイベントページの作り方を提案します。
              </p>
              <button
                type="button"
                className="a-button a-button__top a-button--orange my-4 w-50"
                onClick={handleReviseClick}
              >
                添削する
              </button>
            </div>
          ) : (
            // 添削結果の表示
            <>
              <div ref={resultRef} className="row justify-content-between p-3_5"></div>
              <h5>添削結果</h5>
              <p className="mt-3 text-fw-bold text-fz-16px">
                総合得点（100点満点）
              </p>
              <div className="mt-3 text-fz-15px">{point}</div>
              <p className="mt-5 text-fw-bold text-fz-16px">
                各項目ごとの得点＋コメント（強み・弱み）
              </p>
              <div className="mt-3 text-fz-15px">{formatText(comment)}</div>
              <p className="mt-5 text-fw-bold text-fz-16px">
                総評（集客しやすさ／参加率アップに向けた具体的な改善案）
              </p>
              <div className="mt-3 text-fz-15px">{formatText(overall)}</div>

              <h5 className="mt-5">修正案</h5>
              <div className="d-flex justify-content-between align-items-baseline mt-3">
                <p className="text-fw-bold text-fz-16px">
                  イベント名
                </p>
                <button
                  type="button"
                  className="btn btn-link text-gray text-fz-14px"
                  onClick={() => copyToClipboard(titleRef)}
                >
                  <img 
                    src="/img/content_copy.png" 
                    alt="コピーアイコン" 
                    style={{ width: 18, height: 18, marginRight: 3 }} 
                  />
                  <span style={{ textDecoration: 'underline' }}>
                  クリップボードにコピー
                  </span>
                </button>
              </div>
              <textarea
                ref={titleRef}
                className="form-control mb-3 text-fz-15px"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
              />
              <div className="d-flex justify-content-between align-items-baseline mt-3">
                <p className="text-fw-bold text-fz-16px">イベントの説明</p>
                <button
                  type="button"
                  className="btn btn-link text-gray text-fz-14px"
                  onClick={() => copyToClipboard(bodyRef)}
                >
                  <img 
                    src="/img/content_copy.png" 
                    alt="コピーアイコン" 
                    style={{ width: 18, height: 18, marginRight: 3 }} 
                  />
                  <span style={{ textDecoration: 'underline' }}>
                  クリップボードにコピー
                  </span>
                </button>
              </div>
              <textarea
                ref={bodyRef}
                className="form-control text-fz-15px"
                value={eventBody}
                onChange={(e) => setEventBody(e.target.value)}
                rows={15}
              />
              <div
                className="mt-5 text-right"
                style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
              >
                <button
                  type="button"
                  className="a-button a-button--sm a-button--orange mb-5"
                  style={{ borderRadius: 4 }}
                  onClick={handleReviseClick}
                >
                  もう一度添削する
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <ModalContentBaseXl ref={modalRef} previewContent={previewContent} modalContent={modalContent} />
      <ModalAlert
        ref={errorModalRef}
        id="modal-revised-error"
        title="自動添削失敗"
        content="タイトルと説明の自動添削に失敗しました。時間をおいてもう一度お試しください。"
      />
      <ModalAlert
        ref={rateLimitModalRef}
        id="modal-rate-limit-error"
        title="自動添削失敗"
        content="1日の利用数を超過しました。24時間後にもう一度お試しください。"
      />
      <ModalLoader ref={loaderRef} message="約1分お待ちください" dummyProgressShown />
    </React.Fragment>
  );
};
